import { uuidv7 } from "uuidv7"

import type { SearchSort } from "~/models/polyschema/SearchSort"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { PropertyId } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendSearchAutocompleteRequest } from "~/classes/handlers/search/autocomplete"
import { StaticSearch } from "~/classes/search/StaticSearch"

class InstantSearch extends GlobalComposable<{
  visible: boolean
  focused: boolean
  expanded: boolean
  mode: "autocomplete" | "search" | "path" | "generate"
  loading: boolean
  query: string
  facetMenuOpen: boolean
  facets: SearchFacet[]
  sorts: SearchSort[]
  /** If defined, place in the file picker view */
  nearFileSelection?: StaticFile
  nearFileSelectionMenuOpen: boolean
  /** If defined, place in the folder in view */
  folderInSelection?: StaticFile
  folderInSelectionMenuOpen: boolean
  tagSelectionMenusOpen: Record<string, boolean>
  /** If defined, we have a keyboard selection */
  keyboardSelection?: number
  /** If defined, we are in file picker mode */
  filePickerResults?: StaticFile[]
  folderPickerResults?: StaticFile[]

  autocompleteText: {
    searchId?: SearchId
    response?: SearchAutocompleteResponseDatagram
  }
  autocompleteSmart: {
    searchId?: SearchId
    response?: SearchAutocompleteResponseDatagram
  }

  autocompleteLastRequest?: string
  autocompleteLastTimestamp?: number
  autocompleteServerThrottle?: any

  /** @deprecated replace with grouped autocomplete version */
  autocompleteCached?: StaticFile[]
  /** @deprecated replace with grouped autocomplete version */
  autocompleteServer?: StaticFile[]
}> {
  #AUTOCOMPLETE_INPUT_THROTTLE_MS = 500
  #AUTOCOMPLETE_INPUT_MAX_DEBOUNCE_MS = 3000

  override defaultState = () => ({
    visible: false,
    focused: false,
    expanded: false,
    mode: "search" as "autocomplete" | "search" | "path" | "generate",
    loading: false,
    query: "",
    facetMenuOpen: false,
    facets: [],
    sorts: [],
    nearFileSelectionMenuOpen: false,
    folderInSelectionMenuOpen: false,
    autocompleteRequests: new Set<string>(),
    autocompleteText: {},
    autocompleteSmart: {},

    tagSelectionMenusOpen: {},
  })

  override onCreate = () => {
    const { addInterfaceEventListener } = useEvents()
    addInterfaceEventListener("searchAutocompleteResponse", this.onAutocompleteResponse)

    // TODO: Is there a more ergonomic way to say "close this when you navigate away"?
    addInterfaceEventListener("afterNavigatedToConversation", this.closeInstantSearch)
    addInterfaceEventListener("afterNavigatedToFolder", this.closeInstantSearch)
    addInterfaceEventListener("afterNavigatedToNonFolderFile", this.closeInstantSearch)
    addInterfaceEventListener("afterNavigatedToSearch", this.closeInstantSearch)
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("searchAutocompleteResponse", this.onAutocompleteResponse)

    removeInterfaceEventListener("afterNavigatedToConversation", this.closeInstantSearch)
    removeInterfaceEventListener("afterNavigatedToFolder", this.closeInstantSearch)
    removeInterfaceEventListener("afterNavigatedToNonFolderFile", this.closeInstantSearch)
    removeInterfaceEventListener("afterNavigatedToSearch", this.closeInstantSearch)
  }

  isInstantSearchOpen = () => {
    return this.state.value.visible
  }

  isInstantSearchFocused = () => {
    return this.state.value.visible && this.state.value.focused
  }

  isInstantSearchExpanded = () => {
    return this.state.value.visible && this.state.value.expanded
  }

  openInstantSearch = () => {
    this.state.value.loading = false
    this.state.value.visible = true
  }

  closeInstantSearch = () => {
    const { currentRootFolder, currentSearch } = useNavigation()
    const { onNavigatedToFolder, onNavigatedToSearch, switchCollectionType } = useCollectionView()

    if (!this.state.value.visible)
      return

    // Investigate why just clearing the state isn't enough. Seems to have some issue with
    // the state getting detached?
    this.state.value.visible = false
    this.state.value.focused = false
    this.clearState()

    if (isDefined(currentRootFolder.value)) {
      this.debug("Navigating to current root folder", currentRootFolder.value.id)
      switchCollectionType("folder")
      onNavigatedToFolder(currentRootFolder.value.asStatic())
    }
    else if (isDefined(currentSearch.value)) {
      this.debug("Navigating to current search", currentSearch.value.id)
      switchCollectionType("search-results")
      onNavigatedToSearch(currentSearch.value.asStatic())
    }
    else {
      this.error("No current root folder or search, state likely fragmented")
    }
  }

  onFocusInstantSearch = () => {
    this.state.value.focused = true
  }

  onBlurInstantSearch = () => {
    this.state.value.focused = false
  }

  openInstantSearchWithFileNear = (file: StaticFile) => {
    this.clearState()
    this.addFileSimilarityFacet(file)
    this.openInstantSearch()
  }

  openInstantSearchWithFileIn = (folder: StaticFile) => {
    this.clearState()
    this.addFolderInFacet(folder)
    this.openInstantSearch()
  }

  searchFacetType = (facet: SearchFacet): SearchFacetTypeKey => {
    if ("exists" in facet.facetType)
      return "exists"
    else if ("equals" in facet.facetType)
      return "equals"
    else if ("notEquals" in facet.facetType)
      return "notEquals"
    else if ("startsWith" in facet.facetType)
      return "startsWith"
    else if ("contains" in facet.facetType)
      return "contains"
    else if ("fileIn" in facet.facetType)
      return "fileIn"
    else if ("fileOnlyIn" in facet.facetType)
      return "fileOnlyIn"
    else if ("greaterThan" in facet.facetType)
      return "greaterThan"
    else if ("greaterThanEqual" in facet.facetType)
      return "greaterThanEqual"
    else if ("lessThan" in facet.facetType)
      return "lessThan"
    else if ("lessThanEqual" in facet.facetType)
      return "lessThanEqual"
    else if ("afterDate" in facet.facetType)
      return "afterDate"
    else if ("onOrAfterDate" in facet.facetType)
      return "onOrAfterDate"
    else if ("beforeDate" in facet.facetType)
      return "beforeDate"
    else if ("onOrBeforeDate" in facet.facetType)
      return "onOrBeforeDate"
    else if ("nearFile" in facet.facetType)
      return "nearFile"
    else if ("nearText" in facet.facetType)
      return "nearText"
    else if ("nearColors" in facet.facetType)
      return "nearColors"
    else if ("hasTag" in facet.facetType)
      return "hasTag"
    else throw new Error(`Unknown facet type: ${facet.facetType}`)
  }

  searchFacetTypeName = (key: SearchFacetTypeKey) => {
    if (key === "exists")
      return "exists"
    else if (key === "equals")
      return "equals"
    else if (key === "notEquals")
      return "is not equal to"
    else if (key === "startsWith")
      return "starts with"
    else if (key === "contains")
      return "contains"
    else if (key === "fileIn")
      return "file in"
    else if (key === "fileOnlyIn")
      return "file only in"
    else if (key === "greaterThan")
      return "is greater than"
    else if (key === "greaterThanEqual")
      return "is greater than or equal to"
    else if (key === "lessThan")
      return "is less than"
    else if (key === "lessThanEqual")
      return "is less than or equal to"
    else if (key === "afterDate")
      return "is after date"
    else if (key === "onOrAfterDate")
      return "is on or after date"
    else if (key === "beforeDate")
      return "is before date"
    else if (key === "onOrBeforeDate")
      return "is on or before date"
    else if (key === "nearFile")
      return "near file"
    else if (key === "nearText")
      return "files similar to text"
    else if (key === "nearColors")
      return "near colors"
    else if (key === "hasTag")
      return "has tag"
    else throw new Error(`Unknown facet type: ${key}`)
  }

  searchFacetValue = (facet: SearchFacet) => {
    if ("exists" in facet.facetType)
      return facet.facetType.exists
    else if ("equals" in facet.facetType)
      return facet.facetType.equals
    else if ("notEquals" in facet.facetType)
      return facet.facetType.notEquals
    else if ("startsWith" in facet.facetType)
      return facet.facetType.startsWith
    else if ("contains" in facet.facetType)
      return facet.facetType.contains
    else if ("fileIn" in facet.facetType)
      return facet.facetType.fileIn
    else if ("fileOnlyIn" in facet.facetType)
      return facet.facetType.fileOnlyIn
    else if ("greaterThan" in facet.facetType)
      return facet.facetType.greaterThan
    else if ("greaterThanEqual" in facet.facetType)
      return facet.facetType.greaterThanEqual
    else if ("lessThan" in facet.facetType)
      return facet.facetType.lessThan
    else if ("lessThanEqual" in facet.facetType)
      return facet.facetType.lessThanEqual
    else if ("afterDate" in facet.facetType)
      return facet.facetType.afterDate
    else if ("onOrAfterDate" in facet.facetType)
      return facet.facetType.onOrAfterDate
    else if ("beforeDate" in facet.facetType)
      return facet.facetType.beforeDate
    else if ("onOrBeforeDate" in facet.facetType)
      return facet.facetType.onOrBeforeDate
    else if ("nearFile" in facet.facetType)
      return facet.facetType.nearFile
    else if ("nearText" in facet.facetType)
      return facet.facetType.nearText
    else if ("nearColors" in facet.facetType)
      return facet.facetType.nearColors
    else if ("hasTag" in facet.facetType)
      return facet.facetType.hasTag
    else throw new Error(`Unknown facet type: ${facet.facetType}`)
  }

  _makeTextAutocompleteRequest = async (query: string, offline: boolean) => {
    const { state: myself } = useSelf()

    if (!myself.value.home || !myself.value.archive)
      return this.error("Instant menu not supported in anon mode")

    // Reuse the search id from our prior request, if we have one. This lets us aggregate
    // responses without fear of losing a prior request
    const id = this.state.value.autocompleteText.searchId
      ?? await StaticSearch.idFromPrehash(StaticSearch.clientPreHashId())

    query = query.trim()
    if (!query.length)
      return this.warn("Empty query for autocomplete request")

    // TODO: "everything" query
    const facets: SearchFacet[] = [{
      propertyId: PropertyId.FILE_NAME,
      facetType: { contains: query },
    }]
    const requestId = generateWorkerRequestId()
    const timestamp = uuidv7()
    const search = StaticSearch.blank(id!)
    search.addSearchLocations(this.searchLocations(), timestamp)
    search.addSearchDefinition({ facets }, timestamp)
    search.addExecutedAt(timestamp)

    const request: SearchAutocompleteRequestDatagram = {
      requestId,
      offline,
      fileSelection: InterfaceSelection.minimumAutocompleteSelection().inner(),
      pathSelection: InterfaceSelection.minimumPathSelection().inner(),
      offset: 0,
      limit: 50,
      search: search.inner(),
    }

    sendSearchAutocompleteRequest(request)
    this.state.value.autocompleteText.searchId = request.search.id
  }

  _makeSmartAutocompleteRequest = async (query: string, offline: boolean) => {
    const { state: myself } = useSelf()

    if (!myself.value.home || !myself.value.archive)
      return this.error("Instant menu not supported in anon mode")

    // Reuse the search id from our prior request, if we have one. This lets us aggregate
    // responses without fear of losing a prior request
    const id = this.state.value.autocompleteSmart.searchId
      ?? await StaticSearch.idFromPrehash(StaticSearch.clientPreHashId())

    query = query.trim()
    if (!query.length)
      return this.warn("Empty query for autocomplete request")

    if (query.length < 3)
      return console.warn("Query too short for autocomplete request", query)

    const facets: SearchFacet[] = [{
      propertyId: PropertyId.FILE_VIRTUAL_PAGE_EMBEDDINGS,
      facetType: { nearText: query },
    }]

    const requestId = generateWorkerRequestId()
    const timestamp = uuidv7()
    const search = StaticSearch.blank(id!)
    search.addSearchLocations(this.searchLocations(), timestamp)
    search.addSearchDefinition({ facets }, timestamp)
    search.addExecutedAt(timestamp)

    const request: SearchAutocompleteRequestDatagram = {
      requestId,
      offline,
      fileSelection: InterfaceSelection.minimumAutocompleteSelection().inner(),
      pathSelection: InterfaceSelection.minimumPathSelection().inner(),
      offset: 0,
      limit: 50,
      search: search.inner(),
    }

    sendSearchAutocompleteRequest(request)
    this.state.value.autocompleteSmart.searchId = request.search.id
  }

  makeAutocompleteRequest = async (query: string, offline: boolean) => {
    this._makeTextAutocompleteRequest(query, offline)
    this._makeSmartAutocompleteRequest(query, offline)
  }

  /**
   * We merge the server-sent and cached results, with server-sent appearing
   * above.
   * TODO: make this a bit more ergonomic since there's a flash in in the UI
   * @deprecated
   */
  autocompleteResults = computed(() => {
    const results = []
    const fileIds = new Set()
    for (const file of this.state.value.autocompleteServer ?? []) {
      if (!fileIds.has(file.id)) {
        results.push(file)
        fileIds.add(file.id)
      }
    }
    for (const file of this.state.value.autocompleteCached ?? []) {
      if (!fileIds.has(file.id)) {
        results.push(file)
        fileIds.add(file.id)
      }
    }
    return results
  })

  moveKeyboardSelectionCursor = (direction: "up" | "down") => {
    console.warn("Out of date")

    const maxIndex = this.autocompleteResults.value.length - 1

    if (!this.autocompleteResults.value.length) {
      this.state.value.keyboardSelection = undefined
      return
    }
    else if (!isDefined(this.state.value.keyboardSelection)) {
      if (direction === "down")
        this.state.value.keyboardSelection = 0
      else if (direction === "up")
        this.state.value.keyboardSelection = maxIndex
      return
    }
    else if (this.state.value.keyboardSelection > maxIndex) {
      if (direction === "down")
        this.state.value.keyboardSelection = 0
      else if (direction === "up")
        this.state.value.keyboardSelection = maxIndex
      return
    }
    else if (direction === "down") {
      if (this.state.value.keyboardSelection < maxIndex)
        this.state.value.keyboardSelection += 1
      else
        this.state.value.keyboardSelection = 0
    }
    else if (direction === "up") {
      if (this.state.value.keyboardSelection > 0)
        this.state.value.keyboardSelection -= 1
      else
        this.state.value.keyboardSelection = maxIndex
    }
    else
      throw new Error("Invalid direction for keyboard selection")
  }

  clearKeyboardSelection = () => {
    this.state.value.keyboardSelection = undefined
  }

  /** When the user enters or deletes some text */
  onInput = async () => {
    if (!this.state.value.query || this.state.value.query === this.state.value.autocompleteLastRequest)
      return console.debug("Skipping empty or redundant query")

    const delta = Date.now() - (this.state.value.autocompleteLastTimestamp ?? 0)
    if (delta > this.#AUTOCOMPLETE_INPUT_MAX_DEBOUNCE_MS || !this.state.value.autocompleteServerThrottle) {
      this.state.value.autocompleteLastRequest = this.state.value.query
      this.state.value.autocompleteLastTimestamp = Date.now()
      this.makeAutocompleteRequest(this.state.value.query, false)
    }

    if (this.state.value.autocompleteServerThrottle)
      clearTimeout(this.state.value.autocompleteServerThrottle)

    this.state.value.autocompleteServerThrottle = setTimeout(async () => {
      delete this.state.value.autocompleteServerThrottle
      this.onInput()
    }, this.#AUTOCOMPLETE_INPUT_THROTTLE_MS)
  }

  onAutocompleteResponse = (response: SearchAutocompleteResponseDatagram) => {
    const { onReceivedAutocompleteResults } = useCollectionView()
    console.log("Autocomplete response", response)

    if (response.offline)
      return this.info("Discarding offline autocomplete response", response)

    if (isDefined(response.error) || !isDefined(response.files) || !isDefined(response.search))
      return this.warn("Autocomplete response with error", response)

    const executedAt = new StaticSearch(response.search).executedAt() ?? 0

    if (response.searchId === this.state.value.autocompleteSmart.searchId) {
      const search = this.state.value.autocompleteSmart.response?.search
      if (search && (new StaticSearch(search).executedAt() ?? 0) > executedAt)
        return this.warn("Discarding out of order autocomplete response", response)
      this.state.value.autocompleteSmart.response = response
    }
    else if (response.searchId === this.state.value.autocompleteText.searchId) {
      const search = this.state.value.autocompleteText.response?.search
      if (search && (new StaticSearch(search).executedAt() ?? 0) > executedAt)
        return this.warn("Discarding out of order autocomplete response", response)
      this.state.value.autocompleteText.response = response
    }
    else
      return this.warn("Autocomplete response with unknown search id", response)

    // aggregate both smart and text search responses.
    const smartFiles = this.state.value.autocompleteSmart.response?.files ?? []
    const textFiles = this.state.value.autocompleteText.response?.files ?? []
    const files = [...smartFiles, ...textFiles].map(file => new StaticFile(file))
    onReceivedAutocompleteResults(files)
  }

  /**
   * TODO: add focus on the facet if it has a focusable element
   */
  addSearchFacet = (facet: SearchFacet) => {
    // can only have one "nearX" facet at a time
    if ("nearColor" in facet.facetType || "nearFile" in facet.facetType)
      this.state.value.facets = this.state.value.facets.filter(
        f => !("nearColor" in f.facetType)
          && !("nearFile" in f.facetType),
      )
    // can only have one "file{Only}In" facet at a time
    if ("fileIn" in facet.facetType || "fileOnlyIn" in facet.facetType)
      this.state.value.facets = this.state.value.facets.filter(
        f => !("fileIn" in f.facetType)
          && !("fileOnlyIn" in f.facetType),
      )
    this.state.value.facets.push(facet)
  }

  addSearchSort = (sort: SearchSort) => {
    // remove other sorts on the same property
    this.state.value.sorts = this.state.value.sorts.filter(s => s.propertyId !== sort.propertyId)
    // add the new sort
    this.state.value.sorts.push(sort)
  }

  addFileSimilarityFacet = (file: StaticFile) => {
    this.addSearchFacet({ propertyId: PropertyId.FILE_VIRTUAL_PAGE_EMBEDDINGS, facetType: { nearFile: file.id } })
    this.state.value.nearFileSelection = file
  }

  addFolderInFacet = (folder: StaticFile) => {
    this.addSearchFacet({ propertyId: PropertyId.FILE_VIRTUAL_PATH, facetType: { fileIn: folder.id } })
    this.state.value.folderInSelection = folder
  }

  addFolderOnlyInFacet = (folder: StaticFile) => {
    this.addSearchFacet({ propertyId: PropertyId.FILE_PARENT_ID, facetType: { fileOnlyIn: folder.id } })
    this.state.value.folderInSelection = folder
  }

  /**
   * When the user initiates a search
   * @deprecated refactoring for autocomplete
   */
  onSearch = async () => {
    const { state: myself } = useSelf()
    const { navigateToSearch } = useNavigation()
    const { createSearch } = useSearch()

    if (!myself.value.home || !myself.value.archive)
      return this.error("Instant menu not supported in anon mode")

    const query = this.state.value.query.trim()
    const facets = [...this.state.value.facets] // shallow copy

    // short queries only have the starts-with and longer queries use all facets
    // TODO: is there a way to make this internal to the search?
    // TODO: do more facet types.
    // TODO: everything search?
    if (query.length > 3)
      facets.push({ propertyId: PropertyId.FILE_NAME, facetType: { contains: query } })
    else if (query.length)
      facets.push({ propertyId: PropertyId.FILE_NAME, facetType: { startsWith: query } })

    const sorts = this.state.value.sorts
    const definition = { facets, sorts }

    const request = await createSearch({
      locations: this.searchLocations(),
      definition,
      pageSize: 50,
      limit: 100,
      offline: false,
    })

    // show a loading indicator until we navigate to the page
    this.state.value.loading = true

    await navigateToSearch(new StaticSearch(request.search), true)
    this.closeInstantSearch()
  }

  /**
   * The default search locations are the user's personal home and the home folders
   * of all the user's shared drives.
   */
  searchLocations = () => {
    const { state: myself } = useSelf()
    const { state: sharedDriveState } = useSharedDrives()

    // if the user selected In/OnlyIn, that folder's root is the only location
    if (isDefined(this.state.value.folderInSelection)) {
      const root = this.state.value.folderInSelection.rootFolder()
      if (!isDefined(root))
        throw new Error("No root folder for folder in selection")
      return [root.id]
    }

    if (!myself.value.home)
      throw new Error("Instant menu not supported in anon mode")

    const locations = [myself.value.home.id]
    for (const { home } of Object.values(sharedDriveState.value.all))
      locations.push(home.id)

    return locations
  }

  matchingTags = async (query: string): Promise<string[]> => {
    const { cacheWorker } = useCache()
    const { findMatchingTags } = useTauri()

    let tags: string[] = []
    if (isDesktop())
      tags = await findMatchingTags(query)
    else
      tags = await cacheWorker().findMatchingTags(query)

    return tags
  }
}

const instantSearch = new InstantSearch("instant-search", theme.colors.teal.hex)
export default function () {
  return instantSearch.use()
}
